import { generatePath } from 'react-router-dom';
import type { AssistantPageContext, AssistantUiAction } from '@/api/assistant/assistantApi.types';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import type { WorkspaceSettingsTabId } from '@/components/practitioner/WorkspaceSettingsPage/tabs';

export const SETTINGS_TAB_SEARCH_PARAMETER = 'tab';
export const TREATMENT_LIBRARY_VIEW_SEARCH_PARAMETER = 'view';
export const PATIENT_TAB_SEARCH_PARAMETER = 'tab';
const DEVICES_VIEW = 'devices';
const ORGANIZATION_TAB_SEARCH_PARAMETER = 'tab';
const MEMBERS_TAB = 'members';
const TEAM_DESTINATION = 'team';
const NEW_SESSION_NOTE_DESTINATION = 'new_session_note';
const NEW_SESSION_NOTE_ID = 'new';
const DEVICE_TARGET_PREFIX = 'settings.devices.';
const RECORD_IDENTIFIER_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const SETTINGS_TAB_BY_DESTINATION: Record<string, WorkspaceSettingsTabId> = {
  'settings.patient_options': 'patientOptions',
  'settings.treatment_library': 'treatmentLibrary',
  'settings.services': 'services',
  'settings.visit_forms': 'consultations',
  'settings.plan_templates': 'planTemplates',
  'settings.consent_templates': 'consentDocuments',
  'settings.branding': 'branding',
  'settings.integrations': 'integrations',
  'settings.notification_preferences': 'notificationPreferences',
  'settings.my_account': 'myAccount',
};

export const STATIC_DESTINATION_PATHS: Record<string, string> = {
  dashboard: APPLICATION_ROUTES.workspaceDashboard,
  clients: APPLICATION_ROUTES.workspaceClients,
  patients: APPLICATION_ROUTES.workspacePatients,
  intake_inbox: APPLICATION_ROUTES.workspaceIntake,
  consents: APPLICATION_ROUTES.workspaceConsents,
  reports: APPLICATION_ROUTES.workspaceReports,
  team: APPLICATION_ROUTES.workspaceOrganizationMembers,
  organization: APPLICATION_ROUTES.workspaceOrganization,
  notifications: APPLICATION_ROUTES.workspaceNotifications,
  chat: APPLICATION_ROUTES.workspaceChat,
  announcements: APPLICATION_ROUTES.workspaceBroadcasts,
  support: APPLICATION_ROUTES.workspaceSupport,
};

function targetsDevices(action: AssistantUiAction): boolean {
  return action.targets.some(function isDeviceTarget(target) {
    return target.target.startsWith(DEVICE_TARGET_PREFIX);
  });
}

function buildSettingsPath(tabId: WorkspaceSettingsTabId, action: AssistantUiAction): string {
  const searchParameters = new URLSearchParams();
  searchParameters.set(SETTINGS_TAB_SEARCH_PARAMETER, tabId);
  if (targetsDevices(action)) {
    searchParameters.set(TREATMENT_LIBRARY_VIEW_SEARCH_PARAMETER, DEVICES_VIEW);
  }
  return APPLICATION_ROUTES.workspaceSettings + '?' + searchParameters.toString();
}

export function buildAssistantDestinationPath(action: AssistantUiAction): string | null {
  const settingsTab = SETTINGS_TAB_BY_DESTINATION[action.destination];
  if (settingsTab !== undefined) {
    return buildSettingsPath(settingsTab, action);
  }
  if (action.destination === 'patient_detail' && action.entityId !== null) {
    const path = generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
      patientId: action.entityId,
    });
    return action.patientTab === null
      ? path
      : path + '?' + PATIENT_TAB_SEARCH_PARAMETER + '=' + action.patientTab;
  }
  if (action.destination === NEW_SESSION_NOTE_DESTINATION && action.entityId !== null) {
    return generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
      patientId: action.entityId,
      noteId: NEW_SESSION_NOTE_ID,
    });
  }
  if (action.destination === 'client_detail' && action.entityId !== null) {
    return generatePath(APPLICATION_ROUTES.workspaceClientDetail, { clientId: action.entityId });
  }
  const staticPath = STATIC_DESTINATION_PATHS[action.destination];
  return staticPath === undefined ? null : staticPath;
}

function findSettingsDestination(tabId: string | null): string {
  const entry = Object.entries(SETTINGS_TAB_BY_DESTINATION).find(function matchesTab(candidate) {
    return candidate[1] === tabId;
  });
  return entry === undefined ? 'settings.patient_options' : entry[0];
}

function emptyContext(routeKey: string | null): AssistantPageContext {
  return { routeKey, entityType: null, entityId: null };
}

export function resolveAssistantPageContext(
  pathname: string,
  search: string
): AssistantPageContext {
  const trimmedPath = pathname.replace(/\/+$/, '');
  if (trimmedPath === APPLICATION_ROUTES.workspaceSettings) {
    const tabId = new URLSearchParams(search).get(SETTINGS_TAB_SEARCH_PARAMETER);
    return emptyContext(findSettingsDestination(tabId));
  }
  if (
    trimmedPath === APPLICATION_ROUTES.workspaceOrganization &&
    new URLSearchParams(search).get(ORGANIZATION_TAB_SEARCH_PARAMETER) === MEMBERS_TAB
  ) {
    return emptyContext(TEAM_DESTINATION);
  }
  const segments = trimmedPath.split('/');
  const candidateId =
    segments.length === 4 && segments[1] === 'workspace' ? segments[3] : undefined;
  const recordId =
    candidateId !== undefined && RECORD_IDENTIFIER_PATTERN.test(candidateId)
      ? candidateId
      : undefined;
  if (recordId !== undefined && segments[2] === 'patients') {
    return { routeKey: 'patient_detail', entityType: 'patient', entityId: recordId };
  }
  if (recordId !== undefined && segments[2] === 'clients') {
    return { routeKey: 'client_detail', entityType: 'client', entityId: recordId };
  }
  const staticEntry = Object.entries(STATIC_DESTINATION_PATHS).find(function matchesPath(entry) {
    return entry[1] === trimmedPath;
  });
  return emptyContext(staticEntry === undefined ? null : staticEntry[0]);
}
